import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

interface SnackbarContextProps {
  open: boolean;
  message: string;
  action?: React.ReactNode;
  persistentOpen: boolean;
  persistentMessage: string;
  persistentAction?: React.ReactNode;
  showSnackbar: (title: string, action?: React.ReactNode) => void;
  showPersistentSnackbar: (title: string, action?: React.ReactNode) => void;
  handleClose: (
    event: Event | React.SyntheticEvent<any, Event>,
    reason: string
  ) => void;
  hideSnackbar: () => void;
  hidePersistentSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextProps | null>(null);

export const useSnackbarContext = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error(
      "useSnackbarContext must be used within a SnackbarProvider"
    );
  }
  return context;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [action, setAction] = useState<React.ReactNode>(undefined);
  const [persistentOpen, setPersistentOpen] = useState(false);
  const [persistentMessage, setPersistentMessage] = useState("");
  const [persistentAction, setPersistentAction] =
    useState<React.ReactNode>(undefined);

  const showSnackbar = useCallback(
    (title: string, action?: React.ReactNode) => {
      setMessage(title);
      setAction(action);
      setOpen(true);
    },
    []
  );

  const hideSnackbar = useCallback(() => {
    setOpen(false);
  }, []);

  const showPersistentSnackbar = useCallback(
    (title: string, action?: React.ReactNode) => {
      setPersistentMessage(title);
      setPersistentAction(action);
      setPersistentOpen(true);
    },
    []
  );

  const hidePersistentSnackbar = useCallback(() => {
    setPersistentOpen(false);
  }, []);

  const handleClose = useCallback(
    (_: Event | React.SyntheticEvent<any>, reason: string) => {
      if (reason === "clickaway") {
        return;
      }
      setOpen(false);
    },
    []
  );

  return (
    <SnackbarContext.Provider
      value={{
        open,
        message,
        action,
        persistentOpen,
        persistentMessage,
        persistentAction,
        showSnackbar,
        showPersistentSnackbar,
        handleClose,
        hideSnackbar,
        hidePersistentSnackbar,
      }}
    >
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const {
    showSnackbar,
    hideSnackbar,
    showPersistentSnackbar,
    hidePersistentSnackbar,
  } = useSnackbarContext();

  return {
    showSnackbar,
    hideSnackbar,
    showPersistentSnackbar,
    hidePersistentSnackbar,
  };
};
